import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-eskerium-blue text-white shadow-lg hover:bg-eskerium-blue/90 hover:shadow-xl hover:shadow-eskerium-blue/20 focus-visible:ring-eskerium-blue",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive",
        outline:
          "border border-white/20 bg-transparent text-white shadow-sm hover:bg-white/10 hover:border-white/30 focus-visible:ring-eskerium-blue",
        secondary:
          "bg-eskerium-gold text-eskerium-dark shadow-lg hover:bg-eskerium-gold/90 hover:shadow-xl hover:shadow-eskerium-gold/20 focus-visible:ring-eskerium-gold",
        ghost: "text-white/80 hover:bg-white/10 hover:text-white",
        link: "text-eskerium-blue underline-offset-4 hover:underline hover:text-eskerium-blue/80",
        premium: "bg-gradient-to-r from-eskerium-gold via-eskerium-gold/90 to-eskerium-gold text-eskerium-dark shadow-lg hover:shadow-xl hover:shadow-eskerium-gold/30 hover:scale-[1.02] transition-transform",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }